import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import * as API from '../../Utils/API.js';
import { ButtonData, CopilotCard } from './CopilotCard';
import { Copilot } from '../../Types/Copilot';
import { Tag } from '../../Types/Tag';
import * as Language from '../../Utils/Language'
import { compareOptionalStrings, modifyWhere } from '../../Utils/Array';
import { CurrencyCode, ExchangeRate } from '../../Utils/Currency';

type Props =
  { copilots: Copilot[]
  , cardButtons: ButtonData[]
  , clickToViewProfile?: boolean
  , displayCurrency: CurrencyCode
  , exchangeRates: ExchangeRate
  , featuredTags: Tag[]
  , allowedTags?: Tag[]
  }

// The CopilotSearchSort component is what we use to render a searchable and sortable list of copilots.
// We use this in both the CopilotList and NewRequest components, and, to enable customization of copilot
// card actions, one of the props of this component is an array of ButtonData, allowing us to set desired
// button actions from the parent component.
export const CopilotSearchSort = (props: Props) => {
  enum SortOptions
    { Featured
    , Rating
    , RecentlyActive
    , NameAsc
    , NameDesc
    , HourlyRateAsc
    , HourlyRateDesc
    }

  const allSortOptions = [
    SortOptions.Featured,
    SortOptions.RecentlyActive,
    SortOptions.Rating,
    SortOptions.NameAsc,
    SortOptions.NameDesc,
    SortOptions.HourlyRateAsc,
    SortOptions.HourlyRateDesc
  ]

  function sortOptionDisplayName(sortOption: SortOptions): string {
    switch (sortOption) {
      case SortOptions.Featured:
        return "Featured CoPilots"
      case SortOptions.Rating:
        return "Rating"
      case SortOptions.RecentlyActive:
        return "Recently Active"
      case SortOptions.NameAsc:
        return "Name (A–Z)"
      case SortOptions.NameDesc:
        return "Name (Z–A)"
      case SortOptions.HourlyRateAsc:
        return "Hourly Rate (Low to High)"
      case SortOptions.HourlyRateDesc:
        return "Hourly Rate (High to Low)"
    }
  }
  // A rate of null or 0 means the CoPilot doesn't advertise one. Those always sort last, in
  // both directions, rather than producing a NaN comparison.
  const compareHourlyRate = (a: Copilot, b: Copilot, direction: 'asc' | 'desc'): number => {
    const rateA = a.hourlyRateCents || 0;
    const rateB = b.hourlyRateCents || 0;

    if (!rateA && !rateB) return 0;
    if (!rateA) return 1;
    if (!rateB) return -1;

    return direction === 'asc' ? rateA - rateB : rateB - rateA;
  }

  // A missing or unparseable lastSeenAt would otherwise yield an Invalid Date, and every
  // comparison against NaN is false — which scrambles the sort rather than failing loudly.
  const lastSeenTime = (copilot: Copilot): number => {
    const timestamp = copilot.lastSeenAt ? new Date(copilot.lastSeenAt).getTime() : 0;

    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  // NOTE: every case sorts a copy. Array.prototype.sort mutates in place, and this is called
  // with both props.copilots and the allCopilots state array.
  const sortCopilots = (copilots: Copilot[]): Copilot[] => {
    switch (sortOption) {
      case SortOptions.Featured:
        if (props.featuredTags.length > 0) {
          return [...copilots].sort((a, b) => {
            // Calculate the number of common tags
            const commonTagsA = a.tags.filter(copilotTag =>
              props.featuredTags.some(featuredTag => featuredTag.id === copilotTag.id)
            ).length;
            const commonTagsB = b.tags.filter(copilotTag =>
              props.featuredTags.some(featuredTag => featuredTag.id === copilotTag.id)
            ).length;

            // If the number of common tags is different, sort by it
            if (commonTagsA !== commonTagsB) {
              return commonTagsB - commonTagsA; // Sort in descending order of common tags
            } else {
              // If the number of common tags is the same, sort by sortScore
              return b.sortScore - a.sortScore; // Sort in descending order of sortScore
            }
          });
        } else {
          // If there are no featured tags, sort by sortScore
          return [...copilots].sort((a, b) => b.sortScore - a.sortScore);
        }
      case SortOptions.Rating:
        return [...copilots].sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
      case SortOptions.RecentlyActive:
        return [...copilots].sort((a, b) => lastSeenTime(b) - lastSeenTime(a));
      case SortOptions.NameAsc:
        return [...copilots].sort((a, b) => compareOptionalStrings(a.preferredName, b.preferredName, 'asc'));
      case SortOptions.NameDesc:
        return [...copilots].sort((a, b) => compareOptionalStrings(a.preferredName, b.preferredName, 'desc'));
      case SortOptions.HourlyRateAsc:
        return [...copilots].sort((a, b) => compareHourlyRate(a, b, 'asc'));
      case SortOptions.HourlyRateDesc:
        return [...copilots].sort((a, b) => compareHourlyRate(a, b, 'desc'));
      default:
        return copilots;
    }
  }

  const [sortOption, setSortOption] = useState(SortOptions.Featured);
  const [allCopilots, setAllCopilots] = useState(sortCopilots(props.copilots));
  const [searchText, setSearchText] = useState('');
  const [filteredCopilots, setFilteredCopilots] = useState(props.copilots);

  // This hook serves to re-sort and re-filter the list of copilots any time one of the following occurs:
  // 1) The underlying list of copilots changes (allCopilots)
  // 2) The sort option is changed
  // 3) The search text is changed
  useEffect(() => {
    const sortedCopilots = sortCopilots(allCopilots);

    const filtered = sortedCopilots.filter((copilot) => {
      // With no search text, every CoPilot matches. Without this, a CoPilot whose name, bio and
      // location are all null and who has no tags or languages matches none of the checks below
      // and is silently dropped from the directory even though nothing was searched for.
      if (!searchText.trim()) return true;

      const searchFields = [
        copilot.preferredName,
        copilot.bio,
        copilot.location,
      ];
      const tagNames = copilot.tags.map((tag) => tag.name);
      const languageNames = copilot.languageCodes.map((code) => Language.name(code));
      return (
        searchFields.some((field) =>
          field?.toLowerCase().includes(searchText.toLowerCase())
        ) ||
        tagNames.some((tagName) =>
          tagName?.toLowerCase().includes(searchText.toLowerCase())
        ) ||
        languageNames.some((language) =>
          language?.toLowerCase().includes(searchText.toLowerCase())
        )
      );
    });

    setFilteredCopilots(filtered);
  }, [allCopilots, sortOption, searchText])

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchText(query);
  }

  const onSelectSortOption = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSortOption = parseInt(event.target.value) as SortOptions;
    setSortOption(selectedSortOption);
  }

  return (
    <div>
      <div className="row mt-3 align-items-center">
        <div className="col-md-8">
          <div className="input-group align-items-center p-0">
            <div className="input-group-prepend ms-2">
              <i className="ai-search"></i>
            </div>
            <input
              className="form-control"
              type="search"
              value={searchText}
              onChange={handleSearchInputChange}
              placeholder="Search by name, bio, or skills…"
            />
          </div>
        </div>
        <div className="col-md-4 mt-2 mt-md-0 text-end">
          <select className="form-select t--copilot-sort" onChange={onSelectSortOption} value={sortOption}>
            {allSortOptions.map((option: SortOptions) => (
              <option key={option} value={option}>
                {sortOptionDisplayName(option)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="row mt-3">
        {filteredCopilots.map((copilot) =>
          <div key={copilot.id} className="mt-2 col-12 col-md-6 col-xl-4 mb-4">
            <CopilotCard
              copilot={copilot}
              cardButtons={props.cardButtons}
              featuredTags={props.featuredTags}
              allowedTags={props.allowedTags}
              displayCurrency={props.displayCurrency}
              exchangeRates={props.exchangeRates}
              clickToViewProfile={props.clickToViewProfile}
              targetBlank={props.clickToViewProfile}
              key={copilot.id}
            />
          </div>
        )}
      </div>
    </div>
  );
}
